import {Component, EventEmitter, Input, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {
  ActiveVehicleActions,
  ApiDataService,
  DealershipService,
  DealerUtilsService,
  OssScheduledService,
  PreferredDealerActions,
  selectActiveVehicle,
  selectGarage,
  selectProfile,
  TrackingService
} from '@ox/data';
import {ActiveAppts, Dealer, Profile, Vehicle, Widget} from '@ox/typings';
import {AppConfigService, AutoDestroy, getAddressString} from '@ox/utils';
import {isEqual, orderBy} from 'lodash';
import {combineLatest, map, Subject, take, takeUntil, tap} from 'rxjs';
import {LocateDealerWebuComponent} from '../locate-dealer/locate-dealer-webu.component';
import {PreferredDealerWebuComponent} from '../preferred-dealer/preferred-dealer-webu.component';

const WIDGET_STATE = {
  UNAUTH: 'unauthenticated',
  PREFERRED: 'hasPreferred',
  NO_PREFERRED: 'noPreferred',
  ERROR: 'cannotLoad',
  CONFIRM_CANCEL: 'confirmCancel',
  UPCOMING_SERVICE: 'upcomingService',
  LOADING: 'loading'
};

@Component({
  selector: 'webu-dealer-need-service',
  templateUrl: './dealer-need-service.component.html',
  providers:[OssScheduledService]
})

export class DealerNeedServiceComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() prop51?:string;
  @Input() prop44?:string;
  @Input() prop53?:string;
  preferredDealer = PreferredDealerWebuComponent;
  locateDealer = LocateDealerWebuComponent;
  pdInputs: {
    profileAddress: string
    preferredDealer: Dealer
  };
  pdOutputs = {
    showLocateDealer: new EventEmitter()
  };
  gcin = '';
  widget = {
    state: WIDGET_STATE.LOADING,
    dealer: {},
    market: this.appConfig.bml.market
  } as Widget;
  isAriaExpanded = false;
  activeVinAppts: Array<any>;
  scheduledServices = {
    currentActiveVehicle: {
      serviceLaneVisit: {
        vehicle: {
          vin: ''
        }
      }
    },
    count: 0
  };
  pendingScheduledServices: any;
  match = false;
  userState = '';
  activeVehicleVin: string;
  cancelThisAppt: ActiveAppts;
  repeat = 1;
  allActiveAppts = false;
  cancelAppointmentFail = false;
  cancelAppointmentSuccess = false;
  getScheduleServiceUrl = '';
  recallCenter = false;
  add = 0;
  hideScheduleServiceLink = false;
  showPreferredDealer = false;
  showDealerLocator = false;
  noApptDisplay = this.appConfig.appSettings.moduleConfig?.['noApptDisplay'] || false;
  gbpe = window.gbpe;
  profile: Profile | undefined;
  profile$ = this.store.select(selectProfile)
    .pipe(takeUntil(this.destroy$), tap((profile) => {
      if (profile) {
        this.profile = profile;
        this.gcin = profile.gcin;
        this.widget.driverType = 'isDefaultDriver';
      } else {
        // user is unauthenticated
        this.profile = undefined;
      }
    }));

  garage: Map<string, Vehicle>;
  garage$ = this.store.select(selectGarage).pipe(takeUntil(this.destroy$), tap((garage) => {
    if (garage) {
      this.garage = garage;
      this.userState = 'authenticated';
    }
  }));
  activeVehicle: Vehicle;
  activeVehicle$ = this.store.select(selectActiveVehicle).pipe(takeUntil(this.destroy$), tap((vehicle) => {
    if (vehicle && !isEqual(this.activeVehicle, vehicle)) {
      this.activeVehicle = vehicle;
      this.activeVehicleVin = vehicle.vin;
    }
  }));
  preferredDealer$ = combineLatest([this.profile$, this.activeVehicle$]).pipe(
    takeUntil(this.destroy$),
    tap(() => {
      if (this.profile && this.activeVehicle) {
        this.loadPreferredDealer();
      } else {
        this.widget.state = WIDGET_STATE.UNAUTH;
      }
    })
  );

  constructor(
    private appConfig: AppConfigService,
    private ocOssScheduledService: OssScheduledService,
    public ocDealershipService: DealershipService,
    private store: Store,
    private api: ApiDataService,
    private ocTrackingService: TrackingService,
    private dealerUtilService: DealerUtilsService
  ) {
    this.activeVehicle$.subscribe();
    this.profile$.subscribe();
    this.garage$.subscribe();
    this.preferredDealer$.subscribe();
  }

  get vehicles(): Vehicle[] {
    return this.garage ? Array.from(this.garage?.values()) : [];
  }

  get userAddress() {
    if (!this.profile || !this.profile.address1) return '';
    const addressArr: string[] = [];
    addressArr.push(this.profile?.address2 ? this.profile.address1.concat(' ').concat(this.profile.address2) : this.profile?.address1 || '');
    addressArr.push(this.profile?.city.concat(',') || '');
    addressArr.push(this.profile?.stateProvince.concat(',') || '');
    addressArr.push(this.profile?.postalCode || '');
    return addressArr.join(' ');
  }

  ngOnInit() {
    this.hideScheduleServiceLink = this.appConfig.appSettings.moduleConfig.locateDealer.hideSpecialServices;
  }

  loadPreferredDealer() {
    if (!this.activeVehicle) return;
    this.ocDealershipService.preferredDealer$(this.activeVehicle)
      .pipe(take(1))
      .subscribe({
        next: (response: Dealer) => {
          this.widget.dealer = response;
          this.widget.state = response.id ? WIDGET_STATE.PREFERRED : WIDGET_STATE.NO_PREFERRED;
          // this.store.dispatch(PreferredDealerActions.update({dealer: response}));
          this.widget.dealer.address = getAddressString({
            address1: response.name,
            address2: response.street,
            city: response.city,
            state: response.state,
            zip: response.zipCode
          });
          this.pdInputs = {
            profileAddress: this.userAddress,
            preferredDealer: this.dealerUtilService.formatDealer(response)
          };
          if (this.widget.state === WIDGET_STATE.PREFERRED && !this.noApptDisplay) {
            this.loadProfileAppts();
          }
        },
        error: () => {
          // TODO: fix error tracking for markets that are NOO
          this.ocTrackingService.trackEvent('event25', 'preferredDealerWidget.cannotLoad');
          this.widget.state = WIDGET_STATE.ERROR;
        }
      });
  }


  getScheduleService() {
    this.getScheduleServiceUrl = this.dealerUtilService.getScheduleServiceUrl(this.widget.dealer);
    window.location.href = this.getScheduleServiceUrl;
  }

  onGetPreferredDealerSuccess() {
    // TODO: GMSA has a driverType for AdditionalDriver that US doesn't have
    this.widget.driverType = 'isDefaultDriver';
  }

  showLocateDealer() {
    this.showDealerLocator = true;
  }

  showPreferrdDealer() {
    this.isAriaExpanded = true;
    this.showPreferredDealer = true;
    this.pdOutputs.showLocateDealer.pipe(
      takeUntil(this.destroy$),
      map((data: boolean) => {
        this.showDealerLocator = data;
      }),
      take(1)
    ).subscribe();
  }

  changeState(nextState: string) {
    if (nextState === 'confirmCancel') {
      this.widget.state = WIDGET_STATE.CONFIRM_CANCEL;
    }
    if (nextState === 'noCancel') {
      this.widget.state = WIDGET_STATE.UPCOMING_SERVICE;
    }
  }

  loadProfileAppts() {
    this.add = 0;

    const success = (data: any) => {
      this.pendingScheduledServices = data;
    };

    const error = (error: any) => {
      console.log(error);
    };

    const displayappts = () => {
      let i = 0;
      this.activeVinAppts = [];

      if (this.pendingScheduledServices) {
        for (i = 0; i < this.pendingScheduledServices.length; i++) {
          if (this.pendingScheduledServices[i].serviceLaneVisit.vehicle.vin === this.activeVehicleVin) {
            this.widget.state = WIDGET_STATE.UPCOMING_SERVICE;
            this.activeVinAppts.push(this.pendingScheduledServices[i]);
            this.activeVinAppts[this.add].dealership.address.postalZone = this.activeVinAppts[this.add].dealership.address.postalZone.substring(0, 5) + '-' + this.activeVinAppts[this.add].dealership.address.postalZone.substring(5);
            this.activeVinAppts[this.add].dealership.departments[0].phoneNumber = '(' + this.activeVinAppts[this.add].dealership.departments[0].phoneNumber.substring(0, 3) + ')' + this.activeVinAppts[this.add].dealership.departments[0].phoneNumber.substring(3, 6) + '-' + this.activeVinAppts[this.add].dealership.departments[0].phoneNumber.substring(6);
            this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start = this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 5);
            if (this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 2) < 12) {
              if (this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 2) >= 0) {
                this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start += ' AM';
              }
            } else {
              if (this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.substring(0, 2) > 12) {
                const convert = this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start.split(':');
                convert[0] = (convert[0] - 12).toString();
                this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start = convert.join(':');
              }
              this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start += ' PM';
            }
            this.adoptDate(this.activeVinAppts[this.add], this.activeVinAppts[this.add].serviceLaneVisit.appointmentDate, this.activeVinAppts[this.add].serviceLaneVisit.appointmentTime.start);
            this.add++;
          }
        }
      }
      this.activeVinAppts = orderBy(this.activeVinAppts, ['appointmentDate', 'appointmentTime'], ['asc', 'asc']);
    };


    this.ocOssScheduledService.upcomingScheduledService(this.widget.market, this.gcin)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => success(response),
        error: (response: any) => error(response),
        complete: displayappts
      });
  }

  changeThis({recordLocator, bac}: { recordLocator: string, bac: string }) {
    window.location.href = `../service/edit?serviceItem=${recordLocator}&bac=${bac}`;
  }

  cancelThis(selectedAppt: any) {
    this.cancelThisAppt = selectedAppt;
    this.widget.state = WIDGET_STATE.CONFIRM_CANCEL;
  }

  toggle(show: string) {
    if (show === 'showall') {
      this.repeat = this.activeVinAppts.length;
      this.allActiveAppts = true;
      // for AODA requirements, we want to be able to place focus on next
      // phone number after we press enter on show all button
      const telephoneDiv = document.getElementById('telephoneNumber1') as HTMLDivElement | null;
      telephoneDiv?.focus();
    }
    if (show === 'showless') {
      this.repeat = 1;
      this.allActiveAppts = false;
      // similar to comment above, we want to bring focus back to show all
      // button to have a logical order
      const showAllDiv = document.getElementById('showAllButton') as HTMLDListElement | null;
      showAllDiv?.focus();
    }
  }


  confirmCancel() {
    const alert = (status: string) => {
      this.cancelAppointmentFail = status === 'fail';
      this.cancelAppointmentSuccess = status === 'success';
    };
    const onError = (err: string) => {
      alert('fail');
    };
    const onSuccess = (response: any) => {
      if (response === 'SUCCESS') {
        this.loadPreferredDealer();
        alert('success');
      } else {
        onError(response);
      }
    };

    this.ocOssScheduledService.cancelScheduledService(this.widget.market, this.cancelThisAppt.serviceLaneVisit)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => onSuccess(response),
        error: (response: any) => onError(response)
      });
  }

  changeActiveVehicle(newVehicle: Vehicle) {
    this.store.dispatch(ActiveVehicleActions.update({vehicle: newVehicle}));
    this.loadProfileAppts();

    // TODO: this might not be used
    // if (newVehicle.make === this.activeVehicleVin) {
    //     $state.go('oc.main.vehicleProfile', {}, {
    //         reload: true
    //     });
    // }
  }


  adoptDate(iteration: any, date: string, time: string) {

    iteration.appointmentDate = date;
    iteration.appointmentTime = time;
    const _date = date.split('-') as string[];
    iteration.day = _date[2];
    iteration.year = _date[0];
    switch (_date[1]) {
      case '01':
        iteration.month = 'scheduled_service_Jan';
        break;
      case '02':
        iteration.month = 'scheduled_service_Feb';
        break;
      case '03':
        iteration.month = 'scheduled_service_Mar';
        break;
      case '04':
        iteration.month = 'scheduled_service_Apr';
        break;
      case '05':
        iteration.month = 'scheduled_service_May';
        break;
      case '06':
        iteration.month = 'scheduled_service_June';
        break;
      case '07':
        iteration.month = 'scheduled_service_July';
        break;
      case '08':
        iteration.month = 'scheduled_service_Aug';
        break;
      case '09':
        iteration.month = 'scheduled_service_Sept';
        break;
      case '10':
        iteration.month = 'scheduled_service_Oct';
        break;
      case '11':
        iteration.month = 'scheduled_service_Nov';
        break;
      case '12':
        iteration.month = 'scheduled_service_Dec';
        break;
    }
  }
}
